import {
    addDays,
    endOfDay,
    endOfMonth,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subMonths,
    subWeeks,
} from 'date-fns';
import { isArray, isEqual } from 'lodash';
import { createStaticRanges, defaultStaticRanges, Range, StaticRange } from 'react-date-range';

type PredefinedRangeName =
    | 'today'
    | 'yesterday'
    | 'thisWeek'
    | 'lastWeek'
    | 'last14Days'
    | 'last30Days'
    | 'thisMonth'
    | 'lastMonth'
    | 'last2Months';

type PredefinedRange = [PredefinedRangeName, string];

type CustomRange = {
    key: string;
    label: string;
    range: () => { startDate: Date; endDate: Date };
};

/**
 * It creates an array of static ranges.
 * @param {Array<PredefinedRange|CustomRange>} ranges - The ranges to be displayed. It can be a custom range.
 *
 * Example:
 * const { t } = useTranslation();
 *
 *
 * getStaticRanges(
 *  ['today', 'hoy'],
 *  ['yesterday', 'ayer'],
 *  {
 *    key: 'last45Days',
 *    label: 'Últimos 45 días',
 *    range: () => { startDate: new Date('2024-04-25'),
 *    endDate: new Date()}
 *  }
 * )
 * */
export function getStaticRanges(...ranges: Array<PredefinedRange | CustomRange>): StaticRange[] {
    const [today, yesterday, week, lastWeek, month, lastMonth] = defaultStaticRanges;

    const thisWeekRange = getThisWeekRange();
    const lastWeekRange = getLastWeekRange();
    const last14DaysRange = getLastNDays(14);
    const last30DaysRange = getLastNDays(30);
    const thisMonthRange = getThisMonthRange();
    const lastMonthRange = getLastNMonthsRange(1);
    const last2MonthsRange = getLastNMonthsRange(2);

    const staticRanges: Record<PredefinedRangeName, StaticRange & { key: PredefinedRangeName }> = {
        today: {
            ...today,
            key: 'today',
        },
        yesterday: {
            ...yesterday,
            key: 'yesterday',
        },
        thisWeek: {
            ...week,
            key: 'thisWeek',
            range: () => thisWeekRange,
            isSelected: rangeIsSelected(thisWeekRange),
        },
        lastWeek: {
            ...lastWeek,
            key: 'lastWeek',
            range: () => lastWeekRange,
            isSelected: rangeIsSelected(lastWeekRange),
        },
        last14Days: {
            key: 'last14Days',
            range: () => last14DaysRange,
            isSelected: rangeIsSelected(last14DaysRange),
        },
        last30Days: {
            key: 'last30Days',
            range: () => last30DaysRange,
            isSelected: rangeIsSelected(last30DaysRange),
        },
        thisMonth: {
            ...month,
            key: 'thisMonth',
            range: () => thisMonthRange,
            isSelected: rangeIsSelected(thisMonthRange),
        },
        lastMonth: {
            ...lastMonth,
            key: 'lastMonth',
            range: () => lastMonthRange,
            isSelected: rangeIsSelected(lastMonthRange),
        },
        last2Months: {
            ...lastMonth,
            key: 'last2Months',
            range: () => last2MonthsRange,
            isSelected: rangeIsSelected(last2MonthsRange),
        },
    };

    const selectedStaticRanges = (ranges.filter((range) => isArray(range)) as PredefinedRange[]).map(
        ([range, label]) => ({
            ...staticRanges[range],
            label: label,
        }),
    );

    const customRanges = (ranges.filter((range) => !isArray(range)) as CustomRange[]).map((range) => {
        return { ...range, isSelected: rangeIsSelected(range.range()) };
    }) as StaticRange[];

    return createStaticRanges([...selectedStaticRanges, ...customRanges]);
}

function getThisWeekRange() {
    const today = startOfDay(new Date());
    return {
        startDate: startOfWeek(today, { weekStartsOn: 1 }),
        endDate: endOfWeek(today, { weekStartsOn: 1 }),
    };
}

function getLastWeekRange() {
    const today = startOfDay(new Date());
    return {
        startDate: subWeeks(startOfWeek(today, { weekStartsOn: 1 }), 1),
        endDate: subWeeks(endOfWeek(today, { weekStartsOn: 1 }), 1),
    };
}

function getLastNDays(numberOfDays: number) {
    const today = startOfDay(new Date());
    return {
        startDate: startOfDay(addDays(today, -numberOfDays + 1)),
        endDate: endOfDay(today),
    };
}

function getThisMonthRange() {
    const today = startOfDay(new Date());
    return {
        startDate: startOfMonth(today),
        endDate: endOfMonth(today),
    };
}

function getLastNMonthsRange(numberOfMonths: number) {
    const today = startOfDay(new Date());
    return {
        startDate: startOfMonth(subMonths(today, numberOfMonths)),
        endDate: endOfMonth(subMonths(today, 1)),
    };
}

function rangeIsSelected(staticRange: { startDate: Date; endDate: Date }): (range: Range) => boolean {
    return (range: Range) => {
        if (!range.startDate || !range.endDate) return false;

        return (
            isEqual(startOfDay(staticRange.startDate), startOfDay(range.startDate)) &&
            isEqual(startOfDay(staticRange.endDate), startOfDay(range.endDate))
        );
    };
}
